import { createClient } from "@sanity/client";

/**
 * The Sanity client, constructed once.
 *
 * Separate from `client.js` (the axios instance for Zatoplan's own API) on
 * purpose: these are two different backends with different auth, different
 * error semantics and different caching, and one module pretending to be both
 * would have to branch on every call.
 *
 * No token. The dataset is public, and a token here would be inlined into the
 * bundle by CRA and therefore readable by anyone — protection that protects
 * nothing, with rotation overhead attached. Verified: an unauthenticated query
 * against this dataset returns 200.
 */

/** Public values. A Sanity project id is meant to appear in client code. */
const projectId = process.env.REACT_APP_SANITY_PROJECT_ID;
const dataset = process.env.REACT_APP_SANITY_DATASET || "production";

/**
 * Pinned, not an env var, and not "today's date".
 *
 * `apiVersion` is a contract between this file and the GROQ below it, so it
 * belongs in code where changing it is a reviewed edit rather than an
 * environment difference that makes staging and production disagree.
 */
const apiVersion = "2026-09-29";

/**
 * `useCdn: true` for every read the app makes. This content is published and
 * cacheable, nobody needs read-your-own-write freshness on an article, and
 * CDN reads draw from a far larger free-tier bucket than uncached ones.
 *
 * The build-time prerender script deliberately does the opposite — see the
 * note in that script. A stale read there would mean a just-published article
 * misses the very deploy its own publish triggered.
 */
export const sanityClient = projectId
  ? createClient({ projectId, dataset, apiVersion, useCdn: true })
  : null;

/**
 * Whether article content can be fetched at all.
 *
 * Null rather than a throwing client when the project id is absent: a missing
 * env var should degrade the journal to an empty state, not take down every
 * route that happens to import this module.
 */
export const hasSanity = Boolean(sanityClient);

export default sanityClient;
